import { api } from '@/lib/api';
import type {
  Attachment,
  ClientChat,
  ClientChatStatus,
  ClientProjectSummary,
  ComposerState,
  Session,
  UserIdentity,
} from './models';

/**
 * Every endpoint this dashboard calls, in one file.
 *
 * The client surface is deliberately small: the whole product is one project
 * list and one chat per project. There is no conversation list and no chat id
 * in any URL - a chat is addressed through its project, which is also why a
 * project a client is not assigned to is a 404 rather than a 403.
 */

const chatBase = (projectId: number) => `/client/projects/${projectId}/chat`;

// -- auth -------------------------------------------------------------------

export interface GoogleAuthStart {
  authorization_url: string;
  state: string;
  expires_at: string;
}

export const startGoogleAuth = (redirectUri: string) =>
  api.post<GoogleAuthStart>('/auth/google/start', { role: 'client', redirect_uri: redirectUri }, { anonymous: true });

export const logout = () => api.post<unknown>('/auth/logout', undefined, { anonymous: true });

export const getMe = () => api.get<UserIdentity>('/client/me');

export type { Session };

// -- projects ---------------------------------------------------------------

export const listProjects = () => api.get<ClientProjectSummary[]>('/client/projects');

// -- chat -------------------------------------------------------------------

export const getChat = (projectId: number, params?: { before_seq?: number; limit?: number }) =>
  api.get<ClientChat>(chatBase(projectId), { query: params });

export interface RunAccepted {
  run_id: number;
  message_seq: number;
  stream_url: string;
  stream_cursor?: string | null;
  status: string;
  client_visible_status?: ClientChatStatus;
  idempotent_replay?: boolean;
}

export const postMessage = (
  projectId: number,
  body: { text: string; attachment_ids?: string[]; client_message_id?: string },
) => api.post<RunAccepted>(`${chatBase(projectId)}/messages`, body);

export const uploadAttachment = (projectId: number, file: File, note?: string, signal?: AbortSignal) => {
  const form = new FormData();
  form.append('file', file);
  if (note) form.append('note', note);
  return api.postForm<Attachment>(`${chatBase(projectId)}/attachments`, form, { signal });
};

export interface AnswerResult {
  ask_id: string;
  resolution: 'answered' | 'denied';
  run_id?: number | null;
  delivered_at: string;
}

export const answerAsk = (
  projectId: number,
  askId: string,
  body: { answer: string; decision?: 'allow' | 'deny' },
) => api.post<AnswerResult>(`${chatBase(projectId)}/answers/${encodeURIComponent(askId)}`, body);

export interface ChatStateResult {
  status: ClientChatStatus;
  composer: ComposerState;
  project_frozen?: boolean;
  done_available?: boolean;
}

export const markDone = (projectId: number, note?: string) =>
  api.post<ChatStateResult>(`${chatBase(projectId)}/done`, note ? { note } : {});

/** The SSE path, relative to the API base. Built here so it cannot drift. */
export const chatStreamPath = (projectId: number) => `${chatBase(projectId)}/stream`;
